import * as React from "react";
import * as Raven from "raven-js";
import { HRQuestionId } from "../../messages/ObjectIds";
import { HRQuestionWithStatus } from "../../models/HR";
import { HRQuestion } from "../../messages/HR";
import "./GenericDateInput.css";
import assertNever from "assert-never";
import { GenericInputHeader } from "./GenericInputHeader";
import { LocalDate, YearMonth } from "js-joda";
import { DatePickerWidget, YearMonthPickerWidget, YearPickerWidget } from "../widgets/DateWidget";
import { Globals } from "../../Globals";

interface GenericDateInputProps {
    hrDateQuestion: HRQuestionWithStatus<HRQuestion.FullDateQuestion | HRQuestion.BirthDateQuestion | HRQuestion.MonthYearDateQuestion | HRQuestion.YearDateQuestion>;
    isHidden: boolean;
    onMobile: boolean;
    onChange: (newVal: string | null, id: HRQuestionId) => void;
    onBlur: (questionId: HRQuestionId) => void;
}

export class GenericDateInput extends React.Component<GenericDateInputProps> {
    constructor(props: GenericDateInputProps) {
        super(props);
        if (props.hrDateQuestion.question.answer !== null) {
            return;
        }

        switch (props.hrDateQuestion.question.type) {
            case "FullDate":
            case "MonthYearDate":
            case "YearDate": {
                const lastPick = Globals.getAnswerByTemplateId(props.hrDateQuestion.question.textId);
                if (lastPick !== null) {
                    this.handleChange(lastPick);
                }
                break;
            }
            case "BirthDate": {
                const lastBirthDate = Globals.getLastBirthDate();
                if (lastBirthDate !== null) {
                    this.handleChange(lastBirthDate);
                }
                break;
            }
            default: {
                return assertNever(props.hrDateQuestion.question);
            }
        }
    }

    handleChange(newVal: string | null) {
        this.props.onChange(newVal, this.props.hrDateQuestion.question.id);
        this.props.onBlur(this.props.hrDateQuestion.question.id);
    }

    getMaxDetailForQuestionType() {
        switch (this.props.hrDateQuestion.question.type) {
            case "BirthDate":
            case "FullDate": {
                return "month";
            }
            case "MonthYearDate": {
                return "year";
            }
            case "YearDate": {
                return "decade";
            }
            default: {
                return assertNever(this.props.hrDateQuestion.question);
            }
        }
    }

    render() {
        const question = this.props.hrDateQuestion.question;
        const status = this.props.hrDateQuestion.status;
        return (
            <div className="control-group" style={this.props.isHidden ? { display: "none" } : { display: "" }}>
                <GenericInputHeader status={status} questionText={question.questionText}/>
                {this.renderWidget()}
            </div>
        );
    }

    tryParseLocalDate(text: string): LocalDate | null {
        try {
            return LocalDate.parse(text);
        } catch (e) {
            Raven.captureException(e);
            return null;
        }
    }

    renderWidget() {
        const question = this.props.hrDateQuestion.question;

        switch (this.props.hrDateQuestion.question.type) {
            case "BirthDate":
            case "FullDate": {
                let text = null;
                if (question.answer !== null) {
                    text = question.answer;

                    // This is here for legacy issues, remove when all data had been converted
                    const LENGTH_OF_LOCAL_DATE = 10;
                    if (text.length > LENGTH_OF_LOCAL_DATE) {
                        text = text.substring(0, LENGTH_OF_LOCAL_DATE);
                    }
                }
                return (
                    <DatePickerWidget
                        className={"GenericDateInput__calendar"}
                        onChange={(e) => this.handleChange(e === null ? null : e.toString())}
                        value={text === null ? null : this.tryParseLocalDate(text)}
                        forMobile={this.props.onMobile}
                    />
                );
            }
            case "MonthYearDate": {
                let text = null;
                if (question.answer !== null) {
                    text = question.answer;

                    // This is here for legacy issues, remove when all data had been converted
                    const LENGTH_OF_YEAR_MONTH = 7;
                    if (text.length > LENGTH_OF_YEAR_MONTH) {
                        text = text.substring(0, LENGTH_OF_YEAR_MONTH);
                    }
                    if (RegExp("[0-9]{4}-[0-9]{2}").test(text) === false) {
                        text = null;
                    }
                }
                return (
                    <YearMonthPickerWidget
                        className={"GenericDateInput__calendar"}
                        onChange={(e) => this.handleChange(e === null ? null : e.toJSON())}
                        value={text === null ? null : YearMonth.parse(text)}
                        forMobile={this.props.onMobile}
                    />
                );
            }
            case "YearDate": {
                let text = null;
                if (question.answer !== null) {
                    text = question.answer;

                    // This is here for legacy issues, remove when all data had been converted
                    const LENGTH_OF_YEAR = 4;
                    if (text.length > LENGTH_OF_YEAR) {
                        text = text.substring(0, LENGTH_OF_YEAR);
                    }
                }
                return (
                    <YearPickerWidget
                        className={"GenericDateInput__calendar"}
                        onChange={(e) => this.handleChange(e === null ? null : e.toString())}
                        value={text === null ? null : +text}
                        forMobile={this.props.onMobile}
                    />
                );
            }
            default: {
                return assertNever(this.props.hrDateQuestion.question);
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/inputs/GenericDateInput.tsx